import api from './api';

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export interface EmendaReportFilters {
  exercicio?: string;
  status?: string;
  secretariaDestino?: string;
  classificacao?: string;
  esfera?: string;
  tipoTransferencia?: string;
  councilorId?: string;
  dataInicio?: string;
  dataFim?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface InstituicaoReportFilters {
  statusOSC?: string;
  modalidade?: string;
  funcaoCodigo?: string;
  dataInicio?: string;
  dataFim?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface RelatorioEmendaItem {
  id: string;
  codigoOficial?: string;
  exercicio?: number;
  statusCicloVida?: string;
  secretariaDestino?: string;
  tipo?: string;
  esfera?: string;
  parlamentarId?: string;
  parlamentarNome?: string;
  instituicaoId?: string;
  instituicaoNome?: string;
  data?: string;
  valor?: number;
  dotacaoOrcamentariaTexto?: string;
  objeto?: string;
}

export interface RelatorioEmendaSecretariaStatusItem {
  secretariaDestino?: string;
  statusCicloVida?: string;
  quantidade: number;
  valorTotal?: number;
}

export interface RelatorioEmendaInstituicaoLinha {
  id: string;
  codigoOficial?: string;
  exercicio?: number;
  parlamentarNome?: string;
  statusCicloVida?: string;
  data?: string;
  valor?: number;
  dotacaoOrcamentariaTexto?: string;
}

export interface RelatorioEmendaInstituicaoItem {
  instituicaoId?: string;
  instituicaoNome?: string;
  dotacaoOrcamentariaTexto?: string;
  total?: number;
  linhas: RelatorioEmendaInstituicaoLinha[];
}

export interface RelatorioInstituicaoItem {
  institutionId: string;
  razaoSocial?: string;
  nomeFantasia?: string;
  cnpj?: string;
  cidade?: string;
  uf?: string;
  statusOSC?: string;
  statusAprovacao?: string;
  modalidadePlano?: string;
  dataCriacao?: string;
  areasAtuacao?: string[];
}

export interface PreProjetoReportFilters {
  exercicio?: string;
  etapa?: string;
  funcaoCodigo?: string;
  dataInicio?: string;
  dataFim?: string;
  selecionadoPor?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface RelatorioPreProjetoResumo {
  porArea: { area: string; valorSolicitado: number; valorContemplado: number; quantidade: number }[];
  porParlamentar: { parlamentar: string; valorContemplado: number; quantidade: number }[];
  saudeSolicitado: number;
  saudeContemplado: number;
  demaisSolicitado: number;
  demaisContemplado: number;
}

export interface RelatorioPreProjetoItem {
  id: string;
  codigo?: string;
  titulo?: string;
  instituicaoId?: string;
  instituicaoNome?: string;
  areaAtuacao?: string;
  funcaoCodigo?: string;
  exercicio?: number;
  etapa?: string;
  classificacao?: string;
  valorSolicitado?: number;
  valorContemplado?: number;
  objeto?: string;
  dataCriacao?: string;
  selecionadoPor?: string;
  dataSelecao?: string;
}

const appendParams = (params: URLSearchParams, filters: Record<string, unknown>) => {
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    params.set(key, String(value));
  });
  return params;
};

const buildParams = (filters: Record<string, unknown>) => appendParams(new URLSearchParams(), filters);

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const reportService = {
  async getEmendas(filters: EmendaReportFilters): Promise<PaginatedResponse<RelatorioEmendaItem>> {
    const response = await api.get('/emendas/relatorio', { params: buildParams(filters) });
    return response.data;
  },

  async exportEmendasXlsx(filters: EmendaReportFilters): Promise<void> {
    const response = await api.get('/emendas/relatorio/xlsx', {
      params: buildParams(filters),
      responseType: 'blob',
    });
    downloadBlob(response.data, 'relatorio-emendas.xlsx');
  },

  async exportEmendasPdf(filters: EmendaReportFilters): Promise<void> {
    const response = await api.get('/emendas/relatorio/pdf', {
      params: buildParams(filters),
      responseType: 'blob',
    });
    downloadBlob(response.data, 'relatorio-emendas.pdf');
  },

  async getResumoEmendasSecretariaStatus(filters: EmendaReportFilters): Promise<RelatorioEmendaSecretariaStatusItem[]> {
    const response = await api.get('/emendas/relatorio/resumo-secretaria-status', {
      params: buildParams(filters),
    });
    return response.data;
  },

  async getEmendasPorInstituicao(filters: EmendaReportFilters): Promise<RelatorioEmendaInstituicaoItem[]> {
    const response = await api.get('/emendas/relatorio/por-instituicao', {
      params: buildParams(filters),
    });
    return response.data;
  },

  async getInstituicoes(filters: InstituicaoReportFilters): Promise<PaginatedResponse<RelatorioInstituicaoItem>> {
    const response = await api.get('/institutions/relatorio', { params: buildParams(filters) });
    return response.data;
  },

  async exportInstituicoesXlsx(filters: InstituicaoReportFilters): Promise<void> {
    const response = await api.get('/institutions/relatorio/xlsx', {
      params: buildParams(filters),
      responseType: 'blob',
    });
    downloadBlob(response.data, 'relatorio-instituicoes.xlsx');
  },

  async exportInstituicoesPdf(filters: InstituicaoReportFilters): Promise<void> {
    const response = await api.get('/institutions/relatorio/pdf', {
      params: buildParams(filters),
      responseType: 'blob',
    });
    downloadBlob(response.data, 'relatorio-instituicoes.pdf');
  },

  async getPreProjetos(filters: PreProjetoReportFilters): Promise<PaginatedResponse<RelatorioPreProjetoItem>> {
    const response = await api.get('/pre-projetos/relatorio', { params: buildParams(filters) });
    return response.data;
  },

  async getPreProjetosResumo(filters: PreProjetoReportFilters): Promise<RelatorioPreProjetoResumo> {
    const response = await api.get('/pre-projetos/relatorio/resumo', { params: buildParams(filters) });
    return response.data;
  },

  async exportPreProjetosXlsx(filters: PreProjetoReportFilters): Promise<void> {
    const response = await api.get('/pre-projetos/relatorio/xlsx', {
      params: buildParams(filters),
      responseType: 'blob',
    });
    downloadBlob(response.data, 'relatorio-pre-projetos.xlsx');
  },

  async exportPreProjetosPdf(filters: PreProjetoReportFilters, graficos = false): Promise<void> {
    const response = await api.get('/pre-projetos/relatorio/pdf', {
      params: buildParams(graficos ? { ...filters, graficos: true } : filters),
      responseType: 'blob',
    });
    downloadBlob(response.data, 'relatorio-pre-projetos.pdf');
  },
};

export default reportService;
